import { formatMoney, formatDate, formatCount, carryLine } from "./billingFormat";

/**
 * The two ways to get more, side by side, for the heads-up and the limit
 * pop-up: move to a bigger plan (unused credits come along, new end date) or
 * add top-up packs to the current plan (same end date).
 *
 * Returns the options the caller can offer; `selected` is "upgrade" | "topup".
 */
export function getMoreOptions(options) {
  if (!options) return { upgrade: null, topup: null };
  const rec = options.recommendation || {};
  const plans = options.plans || {};
  const upgradeId = rec.plan_id && plans[rec.plan_id]?.allowed ? rec.plan_id : null;
  const topup = options.topup?.allowed ? options.topup : null;
  return {
    upgrade: upgradeId ? plans[upgradeId] : null,
    topup,
    packs: Math.max(1, rec.packs || 1),
    preferred: rec.action === "topup" && topup ? "topup" : upgradeId ? "upgrade" : topup ? "topup" : null,
  };
}

export default function GetMoreChoices({ options, selected, onSelect, packs, onPacks }) {
  const { upgrade, topup } = getMoreOptions(options);
  const carry = carryLine(options?.carry);
  const row = (key, title, body, extra) => (
    <button
      type="button"
      key={key}
      onClick={() => onSelect(key)}
      className={`w-full text-left rounded-xl border px-3.5 py-3 flex gap-3 transition-colors ${
        selected === key
          ? "border-[#2F64FF] bg-[#2F64FF]/[0.06] dark:bg-[#2F64FF]/10"
          : "border-[#EBEAED] dark:border-gray-700/80 hover:border-[#2F64FF]/50"
      }`}
    >
      <span
        className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
          selected === key ? "border-[#2F64FF] bg-[#2F64FF] shadow-[inset_0_0_0_3px_white] dark:shadow-[inset_0_0_0_3px_#20202B]" : "border-gray-300 dark:border-gray-600"
        }`}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-gray-900 dark:text-white">{title}</span>
        <span className="block text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">{body}</span>
        {extra}
      </span>
    </button>
  );

  return (
    <div className="space-y-2">
      {upgrade && row(
        "upgrade",
        `${options?.current?.in_date ? "Move to" : "Buy"} ${upgrade.display_name} · ${formatMoney(upgrade.amount, upgrade.currency)}`,
        `${formatCount(upgrade.interviews)} interviews and ${formatCount(upgrade.cvs)} CV screenings until ${formatDate(upgrade.ends_on)}. ${carry}`.trim(),
      )}
      {topup && row(
        "topup",
        `Add interviews · ${formatMoney(topup.pack_price, topup.currency)} for ${topup.interviews_per_pack}`,
        `Each pack adds ${topup.interviews_per_pack} interviews and ${formatCount(topup.cvs_per_pack)} CV screenings, usable until ${formatDate(topup.valid_until)}.`,
        selected === "topup" && onPacks ? (
          <span className="mt-2 inline-flex items-center gap-2 text-[12px] font-semibold text-gray-800 dark:text-gray-200">
            <span
              role="button"
              tabIndex={0}
              aria-label="One pack fewer"
              onClick={(e) => { e.stopPropagation(); onPacks(Math.max(1, packs - 1)); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onPacks(Math.max(1, packs - 1)); } }}
              className="w-6 h-6 rounded-md border border-gray-300 dark:border-gray-600 grid place-items-center"
            >−</span>
            {packs} pack{packs === 1 ? "" : "s"} = {packs * topup.interviews_per_pack} interviews · {formatMoney(topup.pack_price * packs, topup.currency)}
            <span
              role="button"
              tabIndex={0}
              aria-label="One pack more"
              onClick={(e) => { e.stopPropagation(); onPacks(Math.min(topup.max_packs || 10, packs + 1)); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onPacks(Math.min(topup.max_packs || 10, packs + 1)); } }}
              className="w-6 h-6 rounded-md border border-gray-300 dark:border-gray-600 grid place-items-center"
            >+</span>
          </span>
        ) : null,
      )}
    </div>
  );
}
